export const INITIAL_TYPEWRITER_STATE = { msgIdx: 0, charCount: 0, mode: 'typing', pause: 0 }

// Pure: advance the animation by one tick. Modes: 'typing' -> 'pausing' -> 'deleting' -> (next msg) 'typing'
export function advanceTypewriter(state, messages, pauseTicks = 8) {
  let { msgIdx, charCount, mode, pause } = state
  const msg = messages[msgIdx] || ''
  if (mode === 'typing') {
    charCount += 1
    if (charCount >= msg.length) { charCount = msg.length; mode = 'pausing'; pause = pauseTicks }
  } else if (mode === 'pausing') {
    pause -= 1
    if (pause <= 0) mode = 'deleting'
  } else { // deleting
    charCount -= 1
    if (charCount <= 0) { charCount = 0; mode = 'typing'; msgIdx = messages.length ? (msgIdx + 1) % messages.length : 0 }
  }
  return { msgIdx, charCount, mode, pause }
}

// Pure: render the current frame text (the typed prefix of the current message)
export function typewriterText(state, messages) {
  const msg = messages[state.msgIdx] || ''
  return msg.slice(0, state.charCount)
}

// Thin controller: drives advanceTypewriter on an interval and calls onFrame(text + suffix).
// Returns { setSuffix(s), stop() }. suffix lets the caller append a live counter like " (2/4)".
export function createTypewriter({ messages, onFrame, intervalMs = 110, pauseTicks = 8 }) {
  let state = { ...INITIAL_TYPEWRITER_STATE }
  let suffix = ''
  const intervalId = setInterval(() => {
    state = advanceTypewriter(state, messages, pauseTicks)
    onFrame(typewriterText(state, messages) + suffix)
  }, intervalMs)
  return {
    setSuffix(s) { suffix = s || '' },
    stop() { clearInterval(intervalId) }
  }
}

export const GREETINGS = ['Hello!', 'Ahoj!', 'Hola!', 'Bonjour!', 'Hallo!', 'Ciao!', 'Olá!', 'Cześć!', 'Hej!', 'Привіт!', 'こんにちは!', '안녕하세요!']
export const TRANSLATING_PREFIX = 'Translating your bot... '

// Pure state machine. modes: 'delete-initial' -> 'type-message' -> 'pause' -> 'delete-greeting' -> 'type-greeting' -> 'pause' ...
export function advanceTranslating(state, { greetings = GREETINGS, prefix = TRANSLATING_PREFIX, pauseTicks = 10 } = {}) {
  let { mode, text, greetingIdx, pause } = state
  if (mode === 'delete-initial') {
    if (text.length === 0) {
      return { mode: 'type-message', text: '', greetingIdx: 0, pause: 0 }
    }
    // fast at the start (remove several chars), decelerating to 1 char near the end
    const chunk = Math.max(1, Math.ceil(text.length / 6))
    return { mode, text: text.slice(0, Math.max(0, text.length - chunk)), greetingIdx, pause }
  }
  if (mode === 'type-message') {
    const target = prefix + greetings[greetingIdx]
    const next = target.slice(0, text.length + 1)
    return next.length >= target.length ?
      { mode: 'pause', text: next, greetingIdx, pause: pauseTicks } :
      { mode, text: next, greetingIdx, pause }
  }
  if (mode === 'pause') {
    return pause <= 1 ?
      { mode: 'delete-greeting', text, greetingIdx, pause: 0 } :
      { mode, text, greetingIdx, pause: pause - 1 }
  }
  if (mode === 'delete-greeting') {
    if (text.length <= prefix.length) {
      const nextIdx = (greetingIdx + 1) % greetings.length
      return { mode: 'type-greeting', text: prefix, greetingIdx: nextIdx, pause: 0 }
    }
    return { mode, text: text.slice(0, text.length - 1), greetingIdx, pause }
  }
  // 'type-greeting'
  const target = prefix + greetings[greetingIdx]
  const next = target.slice(0, text.length + 1)
  return next.length >= target.length ?
    { mode: 'pause', text: next, greetingIdx, pause: pauseTicks } :
    { mode: 'type-greeting', text: next, greetingIdx, pause }
}

// Thin controller: drives advanceTranslating on an interval, calls onFrame(text).
export function createTranslatingTypewriter({ initialText = '', onFrame, intervalMs = 60, greetings = GREETINGS, prefix = TRANSLATING_PREFIX, pauseTicks = 10 } = {}) {
  let state = { mode: 'delete-initial', text: initialText, greetingIdx: 0, pause: 0 }
  const intervalId = setInterval(() => {
    state = advanceTranslating(state, { greetings, prefix, pauseTicks })
    onFrame(state.text)
  }, intervalMs)
  return { stop() { clearInterval(intervalId) } }
}
